import React, { useState, useEffect } from 'react';
import { LayersIcon, RefreshIcon, DatabaseIcon, CheckIcon, AlertIcon } from './Icons';
import { useNotification } from './Notification';

function ScriptPermissions() {
  const [scripts, setScripts] = useState([]);
  const [permissions, setPermissions] = useState({});
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const { showNotification } = useNotification();

  useEffect(() => {
    fetchScriptsAndPermissions();
  }, []);

  const fetchScriptsAndPermissions = async () => {
    setLoading(true);
    try {
      const token = localStorage.getItem('token');
      
      // Récupérer tous les scripts
      const scriptsResponse = await fetch('/api/logs/scripts', {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      
      if (scriptsResponse.ok) {
        const scriptsData = await scriptsResponse.json();
        setScripts(scriptsData);
        
        // Récupérer les permissions
        const permissionsResponse = await fetch('/api/script-permissions', {
          headers: { 'Authorization': `Bearer ${token}` }
        });
        
        if (permissionsResponse.ok) {
          const permissionsData = await permissionsResponse.json();
          const permissionsMap = {};
          
          // Créer un map des permissions existantes
          permissionsData.forEach(perm => {
            permissionsMap[perm.script] = perm.required_role;
          });
          
          // Pour chaque script, définir la permission (par défaut 'user')
          const allPermissions = {};
          scriptsData.forEach(script => {
            allPermissions[script] = permissionsMap[script] || 'user';
          });
          
          setPermissions(allPermissions);
        }
      }
    } catch (error) {
      console.error('Erreur récupération scripts/permissions:', error);
    } finally {
      setLoading(false);
    }
  };

  const handlePermissionChange = (script, role) => {
    setPermissions(prev => ({
      ...prev,
      [script]: role
    }));
  };

  const handleSavePermissions = async () => {
    setSaving(true);
    try {
      const token = localStorage.getItem('token');
      const response = await fetch('/api/script-permissions', {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({ permissions })
      });

      if (response.ok) {
        showNotification('Permissions enregistrées avec succès', 'success');
      } else {
        const data = await response.json();
        showNotification(`Erreur: ${data.error || 'Échec de l\'enregistrement'}`, 'error');
      }
    } catch (error) {
      console.error('Erreur sauvegarde permissions:', error);
      showNotification('Erreur lors de la sauvegarde', 'error');
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="script-permissions-container">
      <div className="permissions-header">
        <div>
          <h1 style={{ 
            display: 'flex', 
            alignItems: 'center', 
            gap: '12px',
            color: '#e5e7eb',
            fontSize: '28px',
            marginBottom: '10px'
          }}>
            <LayersIcon size={32} />
            Permissions par Script
          </h1>
          <p style={{ color: '#9ca3af', fontSize: '14px' }}>
            Définissez le niveau d'accès requis pour consulter les logs de chaque script
          </p>
        </div>
        <div className="permissions-actions">
          <button 
            className="btn btn-secondary" 
            onClick={fetchScriptsAndPermissions}
            disabled={loading}
          >
            <RefreshIcon size={18} />
            Actualiser
          </button>
          <button 
            className="btn btn-primary" 
            onClick={handleSavePermissions}
            disabled={saving || loading}
          >
            <DatabaseIcon size={18} />
            {saving ? 'Enregistrement...' : 'Enregistrer'}
          </button>
        </div>
      </div>

      {loading ? (
        <div className="loading">
          <div className="spinner"></div>
          <p>Chargement des scripts...</p>
        </div>
      ) : scripts.length === 0 ? (
        <div className="no-scripts">
          <p>Aucun script trouvé</p>
        </div>
      ) : (
        <div className="permissions-grid">
          {scripts.map(script => (
            <div key={script} className="permission-card">
              <div className="permission-card-header">
                <LayersIcon size={20} />
                <h3>{script}</h3>
              </div>
              <div className="permission-options">
                <label className={`permission-option ${permissions[script] === 'user' ? 'selected' : ''}`}>
                  <input
                    type="radio"
                    name={`perm-${script}`}
                    value="user"
                    checked={permissions[script] === 'user'}
                    onChange={(e) => handlePermissionChange(script, e.target.value)}
                  />
                  <span className="option-label">
                    <span className="role-badge user">user</span>
                    <span className="option-description">Accessible à tous les utilisateurs</span>
                  </span>
                </label>
                <label className={`permission-option ${permissions[script] === 'admin' ? 'selected' : ''}`}>
                  <input
                    type="radio"
                    name={`perm-${script}`}
                    value="admin"
                    checked={permissions[script] === 'admin'}
                    onChange={(e) => handlePermissionChange(script, e.target.value)}
                  />
                  <span className="option-label">
                    <span className="role-badge admin">admin</span>
                    <span className="option-description">Réservé aux administrateurs</span>
                  </span>
                </label>
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

export default ScriptPermissions;
